// One project: status (picked by the owner), progress, and its tasks grouped
// by stage. Stages can be added, renamed, moved and removed; the project can
// be saved as a template for the next one.
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { ArrowDown, ArrowLeft, ArrowUp, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '../../components/ui/button'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Dialog } from '../../components/ui/dialog.lib'
import { EmptyState } from '../../components/ui/empty-state'
import { Field } from '../../components/ui/field'
import { Input } from '../../components/ui/input'
import { PageFrame } from '../../components/ui/page-frame'
import { PageHeader } from '../../components/ui/page-header'
import { Select } from '../../components/ui/select'
import { toast } from '../../lib/toast'
import { useT } from '../../i18n'
import { staffTasksI18n } from '../../i18n/shared/staff-tasks'
import {
  addStage,
  deleteProject,
  deleteStage,
  getProject,
  listTasks,
  PROJECT_STATUSES,
  saveAsTemplate,
  staffTaskKeys,
  StaffTaskApiError,
  updateProject,
  updateStage,
  updateTask,
  type ProjectStage,
  type ProjectStatus,
  type StaffTask,
  type TaskStatus,
} from './api'
import { formatDay, tasksBasePath, type TaskShell } from './lib'
import { PrivateLock, ProgressBar, ProjectStatusBadge } from './ProjectBits'
import { MeetingNotesSection } from './MeetingNotes'
import { ProjectFormDialog } from './ProjectFormDialog'
import { SIDEBAR_HEADER, TasksLayout } from './TasksSidebar'
import { useHiddenColumns, type SortState, type TaskColumn } from './columns'
import { ColumnsMenu, TaskTable } from './TaskTable'

// Every task here is in this project; stages are the sections themselves.
const IN_PROJECT: TaskColumn[] = ['project', 'stage']
import { TaskFormDialog } from './TaskFormDialog'

const SURFACE =
  'rounded-[var(--surface-lifted-radius)] border border-[var(--surface-lifted-border)] bg-[var(--surface-lifted-bg)] p-4 shadow-[var(--surface-lifted-shadow)]'
const ICON_BUTTON =
  'inline-flex h-7 w-7 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)] disabled:opacity-40'

type Confirm = { kind: 'project' } | { kind: 'stage'; stage: ProjectStage } | null

export function StaffProjectDetail({ shell }: { shell: TaskShell }) {
  const t = useT(staffTasksI18n)
  const p = t.projects
  const { projectId } = useParams({ strict: false }) as { projectId: string }
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const base = tasksBasePath(shell)
  const [editOpen, setEditOpen] = useState(false)
  const [templateOpen, setTemplateOpen] = useState(false)
  const [newTask, setNewTask] = useState<{ stageId: string | null } | null>(null)
  const [confirm, setConfirm] = useState<Confirm>(null)
  const [stageName, setStageName] = useState('')

  const detail = useQuery({
    queryKey: staffTaskKeys.project(projectId),
    queryFn: () => getProject(projectId),
  })
  const [sort, setSort] = useState<SortState>(null)
  const [hidden, toggleColumn] = useHiddenColumns()
  const taskParams = { project_id: projectId, sort: sort?.key, dir: sort?.dir, limit: 200 }
  const tasks = useQuery({
    queryKey: staffTaskKeys.list(taskParams),
    queryFn: () => listTasks(taskParams),
  })
  const refresh = () => void queryClient.invalidateQueries({ queryKey: staffTaskKeys.all })
  const onError = () => toast.error(t.toasts.error)

  const setStatus = useMutation({
    mutationFn: (status: ProjectStatus) => updateProject(projectId, { status }),
    onSuccess: refresh,
    onError,
  })
  const setTaskStatus = useMutation({
    mutationFn: ({ id, next }: { id: string; next: TaskStatus }) => updateTask(id, { status: next }),
    onSuccess: refresh,
    onError,
  })
  const createStage = useMutation({
    mutationFn: () => addStage(projectId, stageName.trim()),
    onSuccess: () => {
      setStageName('')
      refresh()
    },
    onError,
  })
  const changeStage = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { name?: string; position?: number } }) =>
      updateStage(projectId, id, body),
    onSuccess: refresh,
    onError,
  })
  const removeStage = useMutation({
    mutationFn: (id: string) => deleteStage(projectId, id),
    onSuccess: () => {
      setConfirm(null)
      refresh()
    },
    onError,
  })
  const remove = useMutation({
    mutationFn: () => deleteProject(projectId),
    onSuccess: () => {
      toast.success(p.toasts.deleted)
      refresh()
      void navigate({ to: base, search: { view: backView } as never })
    },
    onError,
  })

  const folderOf = detail.data?.project.folder_id
  const backView = folderOf ? `folder:${folderOf}` : 'projects'
  const back = (
    <Link
      to={base}
      search={{ view: backView } as never}
      className="inline-flex items-center gap-1 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
      {p.detail.back}
    </Link>
  )

  const layout = (children: React.ReactNode) => (
    <TasksLayout shell={shell} active={{ projectId }}>
      {children}
    </TasksLayout>
  )

  if (detail.isError) {
    const missing = detail.error instanceof StaffTaskApiError && detail.error.status === 404
    return (
      <PageFrame>
        {layout(
          <>
            {back}
            <EmptyState icon="taskComplete" title={missing ? p.detail.notFound : t.loadError} />
          </>,
        )}
      </PageFrame>
    )
  }
  if (!detail.data) return <PageFrame aria-busy>{layout(back)}</PageFrame>

  const { project, stages } = detail.data
  const items = tasks.data?.items ?? []
  const stageIds = new Set(stages.map((stage) => stage.id))
  const loose = items.filter((task) => !task.stage_id || !stageIds.has(task.stage_id))
  const target = formatDay(project.target_date)
  const meta = [
    project.owner ? p.detail.ownerLine(project.owner.display_name) : p.noOwner,
    target ? p.detail.targetLine(target) : p.noTarget,
  ].join(' · ')

  const renderTasks = (list: StaffTask[]) =>
    list.length === 0 ? (
      <p className="m-0 text-sm text-[var(--color-text-muted)]">{p.detail.noTasks}</p>
    ) : (
      <TaskTable
        shell={shell}
        items={list}
        hidden={hidden}
        omit={IN_PROJECT}
        sort={sort}
        onSort={setSort}
        testId="staff-project-task-table"
        onStatus={(task, next) => setTaskStatus.mutate({ id: task.id, next })}
      />
    )

  return (
    <PageFrame data-testid="staff-project-detail">
      <TasksLayout shell={shell} active={{ projectId }}>
      {back}
      <PageHeader
        className={SIDEBAR_HEADER}
        title={
          project.is_private ? (
            <span className="inline-flex items-center gap-2">
              {project.name}
              <PrivateLock memberCount={project.members?.length} className="h-4 w-4" />
            </span>
          ) : (
            project.name
          )
        }
        metadata={
          project.is_private
            ? `${meta} · ${(project.members ?? []).map((m) => m.display_name).join(', ')}`
            : meta
        }
        actions={
          <>
            <Select
              aria-label={p.form.statusLabel}
              className="w-40"
              value={project.status}
              onChange={(e) => setStatus.mutate(e.target.value as ProjectStatus)}
              data-testid="staff-project-status"
            >
              {PROJECT_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {p.status[value]}
                </option>
              ))}
            </Select>
            <Button variant="outline" onClick={() => setEditOpen(true)}>
              {p.detail.edit}
            </Button>
            {!project.is_private && (
              <Button variant="outline" onClick={() => setTemplateOpen(true)}>
                {p.detail.saveTemplate}
              </Button>
            )}
            {project.can_delete && (
              <Button variant="outlineDestructive" onClick={() => setConfirm({ kind: 'project' })}>
                {p.detail.delete}
              </Button>
            )}
          </>
        }
      />

      <section className={SURFACE}>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <ProjectStatusBadge status={project.status} />
          {project.tasks_overdue > 0 && project.status !== 'done' && (
            <span className="font-medium text-[var(--color-danger)]">
              {p.overdueTasks(project.tasks_overdue)}
            </span>
          )}
          <ProgressBar
            className="w-40"
            done={project.tasks_done}
            total={project.tasks_total}
            label={p.tasksDone(project.tasks_done, project.tasks_total)}
          />
          <span className="text-[var(--color-text-muted)]">
            {p.tasksDone(project.tasks_done, project.tasks_total)}
          </span>
        </div>
        {project.description && (
          <p className="m-0 mt-3 whitespace-pre-wrap text-sm text-[var(--color-text)]">
            {project.description}
          </p>
        )}
      </section>

      <MeetingNotesSection projectId={project.id} shell={shell} />

      <div className="flex justify-end">
        <ColumnsMenu hidden={hidden} onToggle={toggleColumn} omit={IN_PROJECT} />
      </div>

      {stages.map((stage, index) => (
        <StageSection
          key={stage.id}
          stage={stage}
          index={index}
          last={index === stages.length - 1}
          onRename={(name) => changeStage.mutate({ id: stage.id, body: { name } })}
          onMove={(position) => changeStage.mutate({ id: stage.id, body: { position } })}
          onRemove={() => setConfirm({ kind: 'stage', stage })}
          onAddTask={() => setNewTask({ stageId: stage.id })}
        >
          {renderTasks(items.filter((task) => task.stage_id === stage.id))}
        </StageSection>
      ))}

      {(loose.length > 0 || stages.length === 0) && (
        <section className={SURFACE} aria-label={p.detail.noStage}>
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 className="m-0 text-sm font-semibold">{p.detail.noStage}</h2>
            <Button size="sm" variant="outline" onClick={() => setNewTask({ stageId: null })}>
              <Plus className="mr-1 h-4 w-4" aria-hidden />
              {t.newTask}
            </Button>
          </div>
          {renderTasks(loose)}
        </section>
      )}

      <form
        className="flex max-w-md gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          if (stageName.trim()) createStage.mutate()
        }}
      >
        <Input
          value={stageName}
          maxLength={120}
          placeholder={p.detail.stagePlaceholder}
          aria-label={p.detail.stagePlaceholder}
          onChange={(e) => setStageName(e.target.value)}
        />
        <Button type="submit" variant="outline" disabled={!stageName.trim() || createStage.isPending}>
          {p.detail.addStage}
        </Button>
      </form>

      <ProjectFormDialog open={editOpen} onOpenChange={setEditOpen} project={project} />
      <TaskFormDialog
        open={newTask !== null}
        onOpenChange={(open) => !open && setNewTask(null)}
        initialProjectId={project.id}
        initialStageId={newTask?.stageId ?? null}
      />
      <SaveTemplateDialog
        open={templateOpen}
        onOpenChange={setTemplateOpen}
        projectId={project.id}
        defaultName={project.name}
      />

      <Dialog open={confirm !== null} onOpenChange={(open) => !open && setConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirm?.kind === 'stage'
                ? p.detail.removeStage(confirm.stage.name)
                : p.detail.deleteConfirmTitle}
            </DialogTitle>
            <DialogDescription>
              {confirm?.kind === 'stage'
                ? p.detail.removeStageConfirm(confirm.stage.name)
                : project.is_private
                  ? t.privacy.deleteBody
                  : p.detail.deleteConfirmBody}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>
              {t.form.cancel}
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending || removeStage.isPending}
              onClick={() =>
                confirm?.kind === 'stage' ? removeStage.mutate(confirm.stage.id) : remove.mutate()
              }
            >
              {confirm?.kind === 'stage' ? p.detail.removeStage(confirm.stage.name) : p.detail.delete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </TasksLayout>
    </PageFrame>
  )
}

function StageSection({
  stage,
  index,
  last,
  onRename,
  onMove,
  onRemove,
  onAddTask,
  children,
}: {
  stage: ProjectStage
  index: number
  last: boolean
  onRename: (name: string) => void
  onMove: (position: number) => void
  onRemove: () => void
  onAddTask: () => void
  children: React.ReactNode
}) {
  const t = useT(staffTasksI18n)
  const p = t.projects
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(stage.name)

  return (
    <section
      className={SURFACE}
      aria-label={stage.name}
      data-testid={`staff-project-stage-${stage.id}`}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {editing ? (
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              if (name.trim() && name.trim() !== stage.name) onRename(name.trim())
              setEditing(false)
            }}
          >
            <Input
              autoFocus
              value={name}
              maxLength={120}
              aria-label={p.detail.renameStage(stage.name)}
              onChange={(e) => setName(e.target.value)}
            />
            <Button type="submit" size="sm">
              {t.form.save}
            </Button>
          </form>
        ) : (
          <h2 className="m-0 text-sm font-semibold">
            {index + 1} · {stage.name}
          </h2>
        )}
        <span className="text-xs tabular-nums text-[var(--color-text-muted)]">
          {p.progress(stage.tasks_done, stage.tasks_total)}
        </span>
        <div className="ml-auto flex items-center gap-0.5">
          <button
            type="button"
            className={ICON_BUTTON}
            aria-label={p.detail.renameStage(stage.name)}
            onClick={() => {
              setName(stage.name)
              setEditing((open) => !open)
            }}
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </button>
          <button
            type="button"
            className={ICON_BUTTON}
            aria-label={p.detail.moveUp(stage.name)}
            disabled={index === 0}
            onClick={() => onMove(index - 1)}
          >
            <ArrowUp className="h-3.5 w-3.5" aria-hidden />
          </button>
          <button
            type="button"
            className={ICON_BUTTON}
            aria-label={p.detail.moveDown(stage.name)}
            disabled={last}
            onClick={() => onMove(index + 1)}
          >
            <ArrowDown className="h-3.5 w-3.5" aria-hidden />
          </button>
          <button
            type="button"
            className={ICON_BUTTON}
            aria-label={p.detail.removeStage(stage.name)}
            onClick={onRemove}
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </button>
          <Button
            size="sm"
            variant="outline"
            className="ml-1"
            aria-label={p.detail.addTask}
            onClick={onAddTask}
          >
            <Plus className="mr-1 h-4 w-4" aria-hidden />
            {t.newTask}
          </Button>
        </div>
      </div>
      {children}
    </section>
  )
}

function SaveTemplateDialog({
  open,
  onOpenChange,
  projectId,
  defaultName,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  defaultName: string
}) {
  const t = useT(staffTasksI18n)
  const p = t.projects
  const queryClient = useQueryClient()
  const [name, setName] = useState(defaultName)
  const save = useMutation({
    mutationFn: () => saveAsTemplate(projectId, name.trim()),
    onSuccess: () => {
      toast.success(p.detail.templateSaved)
      void queryClient.invalidateQueries({ queryKey: staffTaskKeys.templates })
      onOpenChange(false)
    },
    onError: () => toast.error(t.toasts.error),
  })
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (name.trim()) save.mutate()
          }}
        >
          <DialogHeader>
            <DialogTitle>{p.detail.saveTemplate}</DialogTitle>
          </DialogHeader>
          <Field id="staff-project-template-name" label={p.detail.templateName} required>
            <Input value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t.form.cancel}
            </Button>
            <Button type="submit" disabled={!name.trim() || save.isPending}>
              {p.detail.saveTemplate}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
